/* eslint-disable no-param-reassign */
/* eslint-disable no-await-in-loop */
import {
  buildCanvas,
  buildCanvasVideo,
  buildCanvasButton,
  buildCanvasProductSet,
  buildCanvasFooter
} from '../Canvas/CanvasCreator';
import { getCanvasVideo, getCanvas } from '../../fbApi/Canvas';
import { buildAd } from '../PackageBuilder/CampaignBuilder';
import { returnConversionDomainUrl } from '../../utils/urls';

export const replaceValuesInAdBlueprint = (urls, adBlueprint) => {
  const newAdBlueprint = Object.assign({}, adBlueprint);
  urls.urlValues.forEach((url) => {
    newAdBlueprint.headline = newAdBlueprint.headline.replace(
      url.name,
      url.value
    );
    newAdBlueprint.adCopy = newAdBlueprint.adCopy.replace(url.name, url.value);
  });

  return newAdBlueprint;
};

export const replaceUrlsInCarousel = (urls, carouselData) => {
  const carousel = Object.assign({}, carouselData);
  carousel.cards = [];

  urls.urlValues.forEach((url) => {
    carousel.body = carousel.body.replace(url.name, url.value);
  });

  carouselData.cards.forEach((card) => {
    const newCard = Object.assign({}, card);
    if (newCard.link) {
      const url = urls.urlValues.find((urlVal) => urlVal.name === newCard.link);

      if (url) {
        newCard.link = url.value;
      }
    }
    carousel.cards.push(newCard);
  });

  return carousel;
};

export const replaceUrlsInCanvas = (urls, canvasData) => {
  canvasData.canvasElements.forEach((element) => {
    if (element.imageTargetUrl) {
      const url = urls.urlValues.find(
        (urlVal) => urlVal.name === element.imageTargetUrl
      );

      if (url) {
        element.imageTargetUrl = url.value;
      }
    } else if (element.cards) {
      element.cards.forEach((card) => {
        if (card.cardTargetUrl) {
          const url = urls.urlValues.find(
            (urlVal) => urlVal.name === card.cardTargetUrl
          );

          if (url) {
            card.cardTargetUrl = url.value;
          }
        }
      });
    }
  });
};

export const createCanvas = async (parameters) => {
  const elements = [];

  const video = {
    url: parameters.url,
    name: parameters.url,
    videoId: parameters.videoId,
    radioFitVideo: 'FitToWidth'
  };

  const canvasVideo = await buildCanvasVideo(parameters, video);
  elements.push(canvasVideo);
  const canvasVideoData = await getCanvasVideo(canvasVideo, parameters.token);

  const canvasProductSet = await buildCanvasProductSet(parameters);
  elements.push(canvasProductSet);

  const canvasButton = await buildCanvasButton(parameters);

  parameters.child_elements = [canvasButton];

  const canvasFooter = await buildCanvasFooter(parameters);
  elements.push(canvasFooter);

  parameters.is_published = true;
  const result = await buildCanvas(parameters, elements, 'Canvas', '#FFFFFF');
  return {
    canvasId: result,
    videoId: canvasVideoData.video.id
  };
};

export const createCarouselAd = async (parameters) => {
  const childs = [];

  parameters.cards.foreEach((card) => {
    const newCard = {
      link: card.link,
      picture: card.image_url,
      name: card.cardName,
      description: card.description,
      call_to_action: {
        type: card.call_to_action,
        value: {
          link: card.link
        }
      },
      video_id: card.video
    };
    childs.push(newCard);
  });

  const ad = {
    adSetId: parameters.adSetId,
    accountId: parameters.adBlueprint.adAccount,
    name: parameters.name,
    status: parameters.status,
    creative: {
      object_story_spec: {
        page_id: parameters.adBlueprint.page,
        link_data: {
          link: parameters.displayLink,
          message: parameters.body,
          caption: parameters.displayLink,
          child_attachments: childs,
          multi_share_end_card: false,
          multi_share_optimized: false
        }
      },
      degrees_of_freedom_spec:{
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      }
    },
    tracking_specs: [
      {
        'action.type': ['offsite_conversion'],
        fb_pixel: [parameters.pixel]
      }
    ]
  };

  let resultAd = '';
  resultAd = await buildAd(ad, parameters.token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    resultAd = await buildAd(ad, parameters.token);
  }

  return resultAd;
};

export const createCanvasAd = async (parameters) => {
  const ad = {
    adSetId: parameters.adSetId,
    accountId: parameters.adBlueprint.adAccount,
    name: parameters.name,
    status: parameters.status,
    creative: {
      object_story_spec: {
        page_id: parameters.adBlueprint.page,
        video_data: {
          video_id: parameters.adBlueprint.asset,
          title: parameters.adBlueprint.headline,
          message: parameters.adBlueprint.adCopy,
          call_to_action: {
            type: 'LEARN_MORE',
            value: {
              link_caption: parameters.adBlueprint.displayLink,
              link: parameters.canvasUrl
            }
          },
          image_url: parameters.adBlueprint.imageUrl
        }
      },
      degrees_of_freedom_spec:{
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: parameters.urlTags
    },
    tracking_specs: [
      {
        'action.type': ['offsite_conversion'],
        fb_pixel: [parameters.pixel]
      }
    ]
  };

  let resultAd = '';
  resultAd = await buildAd(ad, parameters.token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    resultAd = await buildAd(ad, parameters.token);
  }

  return resultAd;
};

export const createCollectionAd = async (parameters) => {
  if (!parameters.canvasUrl) {
    const canvasParameters = {
      pageId: parameters.pageId,
      token: parameters.pageToken,
      url: parameters.videoUrl,
      videoId: parameters.videoId,
      buttonName: 'Button',
      productSetName: 'ProductSet',
      destinationUrl: parameters.destinationUrl,
      product_set_id: parameters.productSet,
      buttonText: parameters.buttonText,
      footerName: 'Footer Name',
      headline: '{{vehicle.year Aa}} {{vehicle.make Aa}} {{vehicle.model Aa}}',
      description: parameters.description
        ? parameters.description
        : 'At {{vehicle.dealer_name Aa}}'
    };

    const canvasId = await createCanvas(canvasParameters);
    const canvas = await getCanvas(canvasId.canvasId, parameters.token);

    parameters.canvasUrl = canvas.canvas_link;
    parameters.videoId = canvasId.videoId;
  }

  const ad = {
    adSetId: parameters.adSetId,
    accountId: parameters.accountId,
    name: parameters.name,
    status: parameters.status,
    conversion_domain: parameters.conversionDomain, // only defined when camapaign.objective === "CONVERSIONS"
    creative: {
      product_set_id: parameters.productSet,
      object_story_spec: {
        page_id: parameters.pageId,
        video_data: {
          video_id: parameters.videoId,
          title: parameters.title,
          message: parameters.message,
          call_to_action: {
            type: 'LEARN_MORE',
            value: {
              link_caption: parameters.displayLink,
              link: parameters.canvasUrl,
              link_format: 'VIDEO_LPP'
            }
          },
          image_url: parameters.imageUrl,
          retailer_item_ids: ['0', '0', '0', '0']
        }
      },
      degrees_of_freedom_spec:{
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: parameters.urlTags
    },
    tracking_specs: [
      {
        'action.type': ['offsite_conversion'],
        fb_pixel: [parameters.pixel]
      }
    ]
  };

  let resultAd = '';
  resultAd = await buildAd(ad, parameters.token);
  let attempts = 0;

  while (
    attempts < 100 &&
    resultAd.error &&
    resultAd.error.error_user_title === 'Video not ready for use in ad creative'
  ) {
    attempts = attempts + 1;
    resultAd = await buildAd(ad, parameters.token);
  }

  return resultAd;
};

export const createCanvasForCollection = async (collectionParameters) => {
  const canvasParameters = {
    pageId: collectionParameters.pageId,
    token: collectionParameters.pageToken,
    url: collectionParameters.videoUrl,
    videoId: collectionParameters.videoId,
    buttonName: 'Button',
    productSetName: 'ProductSet',
    destinationUrl: collectionParameters.destinationUrl,
    product_set_id: collectionParameters.productSet,
    buttonText: collectionParameters.buttonText,
    footerName: 'Footer Name',
    headline: '{{vehicle.year Aa}} {{vehicle.make Aa}} {{vehicle.model Aa}}',
    description: collectionParameters.description
      ? collectionParameters.description
      : 'At {{vehicle.dealer_name Aa}}'
  };

  const newCanvas = await createCanvas(canvasParameters);
  const { canvasId, videoId: canvasVideoId } = newCanvas || {};
  const retrievedCanvas = await getCanvas(canvasId, collectionParameters.token);
  const { canvas_link: canvasLink } = retrievedCanvas || {};
  const returnData = {
    canvasLink: canvasLink || '',
    canvasVideoId: canvasVideoId || ''
  };

  return Promise.resolve(returnData);
};

export const createCollectionAdWithCanvas = async (
  adParameters,
  canvasParameters
) => {
  const parameters = adParameters;
  const { canvasLink, canvasVideoId } = canvasParameters;
  if (!adParameters.canvasUrl) {
    parameters.canvasUrl = canvasLink;
    parameters.videoId = canvasVideoId;
  }

  const ad = {
    adSetId: parameters.adSetId,
    accountId: parameters.accountId,
    name: parameters.name,
    status: parameters.status,
    creative: {
      product_set_id: parameters.productSet,
      object_story_spec: {
        page_id: parameters.pageId,
        video_data: {
          video_id: parameters.videoId,
          title: parameters.title,
          message: parameters.message,
          call_to_action: {
            type: 'LEARN_MORE',
            value: {
              // link_caption: parameters.displayLink,
              link: parameters.canvasUrl,
              link_format: 'VIDEO_LPP'
            }
          },
          image_url: parameters.imageUrl,
          retailer_item_ids: ['0', '0', '0', '0']
        }
      },
      degrees_of_freedom_spec:{
        creative_features_spec: {
          standard_enhancements: {
            enroll_status: 'OPT_OUT'
          }
        }
      },
      url_tags: parameters.urlTags
    },
    tracking_specs: [
      {
        'action.type': ['offsite_conversion'],
        fb_pixel: [parameters.pixel]
      }
    ],
    conversion_domain: returnConversionDomainUrl({
      destinationUrl: adParameters.destinationUrl,
      campaign: parameters.willUseConversionDomain
        ? { objective: 'CONVERSIONS' }
        : null
    })
  };

  let resultAd = {};
  resultAd = await buildAd(ad, parameters.token);
  let attempts = 0;

  while (
    attempts < 100 &&
    resultAd.error &&
    resultAd.error.error_user_title === 'Video not ready for use in ad creative'
  ) {
    attempts = attempts + 1;
    resultAd = await buildAd(ad, parameters.token);
  }

  return Promise.resolve(resultAd);
};



// WEBPACK FOOTER //
// ./src/screens/AdBuilder/AdCreator.js